/**
 * Copyright 2025 MadeByTatwa LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Detector for Node.js web frameworks (Next.js, Vite, CRA, Angular, etc.)
 */
import type { DetectorResult } from '../types/index.js';
import type { Detector } from './base.js';
import { readPackageJson, fileExists, hasDependency, hasAnyFile } from '../utils/fs.js';
import { detectPackageManager, getRunCommand } from '../utils/pm.js';
import { calculateConfidence, type ConfidenceFactors } from '../core/confidence.js';
import { createDetectorResult } from './base.js';
import { join } from 'path';

export class NodeWebDetector implements Detector {
  name = 'node-web';

  async detect(cwd: string): Promise<DetectorResult> {
    const pkg = await readPackageJson(cwd);
    if (!pkg) {
      return createDetectorResult(this.name, 0);
    }

    const pm = detectPackageManager(cwd);
    const scripts = (pkg.scripts as Record<string, string>) || {};

    // Next.js detection
    if (await hasDependency(cwd, 'next')) {
      const script = scripts.dev || scripts.start;
      if (script) {
        const { cmd, args } = getRunCommand(pm, script.includes('dev') ? 'dev' : 'start');
        
        const factors: ConfidenceFactors = {
          frameworkDependency: 1,
          scriptExists: 1,
          packageJsonExists: 1,
          lockFileExists: fileExists(join(cwd, 'package-lock.json')) || fileExists(join(cwd, 'yarn.lock')) ? 1 : 0,
        };
        
        const confidence = calculateConfidence(factors);
        
        return createDetectorResult(this.name, confidence, {
          cwd,
          pm,
          cmd,
          args,
          detectedType: 'Next.js',
          reasons: [
            'Detected Next.js project',
            `Using package manager: ${pm}`,
            `Running script: ${script}`,
          ],
        });
      }
    }

    // Vite detection
    if (await hasDependency(cwd, 'vite')) {
      const hasViteConfig = hasAnyFile(cwd, [
        'vite.config.ts',
        'vite.config.js',
        'vite.config.mjs',
      ]);

      if (hasViteConfig) {
        const script = scripts.dev || scripts.start;
        if (script) {
          const { cmd, args } = getRunCommand(pm, script.includes('dev') ? 'dev' : 'start');
          
          const factors: ConfidenceFactors = {
            frameworkDependency: 1,
            configFileExists: 1,
            scriptExists: 1,
            packageJsonExists: 1,
          };
          
          const confidence = calculateConfidence(factors);
          
          return createDetectorResult(this.name, confidence, {
            cwd,
            pm,
            cmd,
            args,
            detectedType: 'Vite',
            reasons: [
              'Detected Vite project',
              `Using package manager: ${pm}`,
              `Running script: ${script}`,
            ],
          });
        }
      }
    }

    // Create React App detection
    if (await hasDependency(cwd, 'react-scripts')) {
      const script = scripts.start;
      if (script) {
        const { cmd, args } = getRunCommand(pm, 'start');
        return createDetectorResult(this.name, 85, {
          cwd,
          pm,
          cmd,
          args,
          detectedType: 'Create React App',
          reasons: [
            'Detected Create React App project',
            `Using package manager: ${pm}`,
            'Running react-scripts start',
          ],
        });
      }
    }

    // Angular detection
    if (await hasDependency(cwd, '@angular/core')) {
      const hasAngularJson = fileExists(join(cwd, 'angular.json'));
      if (hasAngularJson) {
        const script = scripts.start || scripts.serve;
        if (script) {
          const { cmd, args } = getRunCommand(pm, script.includes('serve') ? 'serve' : 'start');
          return createDetectorResult(this.name, 90, {
            cwd,
            pm,
            cmd,
            args,
            detectedType: 'Angular',
            reasons: [
              'Detected Angular project',
              `Using package manager: ${pm}`,
              `Running script: ${script}`,
            ],
          });
        }
      }
    }

    // Vue CLI detection
    if (await hasDependency(cwd, '@vue/cli-service')) {
      const script = scripts.serve || scripts.dev;
      if (script) {
        const { cmd, args } = getRunCommand(pm, script.includes('serve') ? 'serve' : 'dev');
        return createDetectorResult(this.name, 85, {
          cwd,
          pm,
          cmd,
          args,
          detectedType: 'Vue CLI',
          reasons: [
            'Detected Vue CLI project',
            `Using package manager: ${pm}`,
            `Running script: ${script}`,
          ],
        });
      }
    }

    // Generic dev/start script fallback
    if (scripts.dev || scripts.start) {
      const script = scripts.dev || scripts.start;
      const { cmd, args } = getRunCommand(pm, script === scripts.dev ? 'dev' : 'start');
      return createDetectorResult(this.name, 50, {
        cwd,
        pm,
        cmd,
        args,
        detectedType: 'Node.js Web Project',
        reasons: [
          'Found dev/start script in package.json',
          `Using package manager: ${pm}`,
          `Running script: ${script}`,
        ],
      });
    }

    return createDetectorResult(this.name, 0);
  }
}

